import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const useUnsavedChanges = ({
  hasUnsavedChanges = false,
  globalNavigationKey = 'createAmenityFormNavigation',
  onLeave = null,
  onStay = null,
} = {}) => {
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const navigate = useNavigate()

  // Handle navigation attempts and show confirmation if there are unsaved changes
  const handleNavigationAttempt = useCallback(
    (navigationFunction) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(() => navigationFunction)
        setShowLeaveConfirmation(true)
        return false
      } else {
        navigationFunction()
        return true
      }
    },
    [hasUnsavedChanges],
  )

  // Handle browser refresh/close
  const handleBeforeUnload = useCallback(
    (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    },
    [hasUnsavedChanges],
  )

  // Handle browser back button
  const handlePopState = useCallback(
    (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        setShowLeaveConfirmation(true)
        setPendingNavigation(() => () => {
          window.history.back()
        })
        // Push current state back to maintain URL
        window.history.pushState(null, '', window.location.pathname)
      }
    },
    [hasUnsavedChanges],
  )

  // Handle staying on the page
  const handleStayOnPage = useCallback(() => {
    setShowLeaveConfirmation(false)
    setPendingNavigation(null)
    if (onStay) {
      onStay()
    }
  }, [onStay])

  // Handle leaving without saving
  const handleLeaveWithoutSaving = useCallback(() => {
    setShowLeaveConfirmation(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
    if (onLeave) {
      onLeave()
    }
  }, [pendingNavigation, onLeave])

  // Custom navigation function that can be called from parent components
  const attemptNavigation = useCallback(
    (path) => {
      return handleNavigationAttempt(() => navigate(path))
    },
    [handleNavigationAttempt, navigate],
  )

  // Function to programmatically clear unsaved changes
  const clearUnsavedChanges = useCallback(() => {
    setShowLeaveConfirmation(false)
    setPendingNavigation(null)
  }, [])

  // Expose navigation function globally if key is provided
  useEffect(() => {
    if (globalNavigationKey) {
      window[globalNavigationKey] = attemptNavigation
      return () => {
        delete window[globalNavigationKey]
      }
    }
  }, [attemptNavigation, globalNavigationKey])

  // Set up event listeners for browser navigation
  useEffect(() => {
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [hasUnsavedChanges, handleBeforeUnload, handlePopState])

  return {
    showLeaveConfirmation,
    handleStayOnPage,
    handleLeaveWithoutSaving,
    clearUnsavedChanges,
  }
}

export default useUnsavedChanges
